import PropTypes from 'prop-types';
import { Input } from 'src/features/tyre-management/components/ui/input';
import { Label } from 'src/features/tyre-management/components/ui/label';
import SelectControl from 'src/features/tyre-management/components/SelectControl';
import {
  ARTICLE_TYPE, CONSUMER_VEHICLE_TYPES, TELEMATICS_TYPES, VEHICLE_CATEGORY, VEHICLE_TYPE,
} from 'src/utils/constants';
import ModulesSection from './ModulesSection';

const LEGACY_SEMI_MOVABLE = { id: 'Semi Movable', name: 'Semi Movable (legacy)' };
const PROVIDERS = ['WAILON', 'VAMOSYS', 'OMNICOMM', 'LOCONAV', 'GEOSAFE'];
const VEHICLE_TYPE_OPTIONS = VEHICLE_TYPE.filter((option) => option.id !== 'Semi Movable');

const FieldError = ({ message }) => (
  message ? <p className="text-xs font-semibold text-red-600" role="alert">{message}</p> : null
);

FieldError.propTypes = { message: PropTypes.string };

const Field = ({
  id, label, required, error, children,
}) => (
  <div>
    <Label htmlFor={id}>
      {label}
      {required ? ' *' : ''}
    </Label>
    {children}
    <FieldError message={error} />
  </div>
);

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const option = (item) => (
  <option key={item.id ?? item.name} value={item.id}>{item.name || item.registration_num}</option>
);

const VehicleFormSections = ({
  form,
  setField,
  setCheckbox,
  options,
  errors,
  assetCodeMappingEnabled,
  isCreate,
  originalType,
  globalTyreEnabled,
  tyreManagementEnabledAt,
}) => {
  const typeOptions = originalType === LEGACY_SEMI_MOVABLE.id
    ? [...VEHICLE_TYPE_OPTIONS, LEGACY_SEMI_MOVABLE]
    : VEHICLE_TYPE_OPTIONS;
  const transferRequired = form.category === 'TRNSFR';
  const strictAssetCodeMapping = assetCodeMappingEnabled && isCreate;
  const usesEngineHours = form.type === 'Non Movable' || form.type === 'Semi Movable';

  return (
    <>
      <section id="edit-identity" className="vd-edit-section vd-section-anchor">
        <div>
          <p className="vd-eyebrow">Identity</p>
          <h2>What identifies this asset</h2>
        </div>
        <div className="vd-form-grid">
          <Field id="registration" label="Registration number" required error={errors.registration_num}>
            <Input id="registration" value={form.registration_num} onChange={setField('registration_num')} required />
          </Field>
          <Field id="asset-code" label="Asset code" required error={errors.asset_code}>
            <Input
              id="asset-code"
              value={form.asset_code}
              onChange={setField('asset_code', strictAssetCodeMapping ? (next) => next : (next) => next.toUpperCase())}
              inputMode={strictAssetCodeMapping ? 'numeric' : undefined}
              required
            />
          </Field>
          <Field id="model" label="Vehicle model" error={errors.model}>
            <Input id="model" value={form.model} onChange={setField('model')} />
          </Field>
          <Field id="movement" label="How the vehicle moves" required error={errors.type}>
            <SelectControl id="movement" value={form.type} onChange={setField('type')} required>
              <option value="">Select type</option>
              {typeOptions.map(option)}
            </SelectControl>
          </Field>
          <Field id="category" label="Work category" error={errors.category}>
            <SelectControl id="category" value={form.category} onChange={setField('category')}>
              <option value="">Not recorded</option>
              {VEHICLE_CATEGORY.map(option)}
            </SelectControl>
          </Field>
          <Field id="ownership" label="Ownership type" error={errors.consumer_vehicle}>
            <SelectControl id="ownership" value={form.consumer_vehicle} onChange={setField('consumer_vehicle')}>
              <option value="">Not recorded</option>
              {CONSUMER_VEHICLE_TYPES.map(option)}
            </SelectControl>
          </Field>
        </div>
      </section>

      <section id="edit-assignment" className="vd-edit-section vd-section-anchor">
        <div>
          <p className="vd-eyebrow">Assignment</p>
          <h2>Where the vehicle belongs</h2>
        </div>
        <div className="vd-form-grid">
          <Field id="site" label="Site" required error={errors.site}>
            <SelectControl id="site" value={form.site} onChange={setField('site')} required searchable searchPlaceholder="Search sites…">
              <option value="">Select site</option>
              {options.sites.map(option)}
            </SelectControl>
          </Field>
          <Field id="group" label="Vehicle group" error={errors.group}>
            <SelectControl id="group" value={form.group || ''} onChange={setField('group')} searchable searchPlaceholder="Search vehicle groups…">
              <option value="">Not assigned</option>
              {options.groups.map(option)}
            </SelectControl>
          </Field>
          <Field id="department" label="Department" error={errors.department}>
            <SelectControl id="department" value={form.department || ''} onChange={setField('department')} searchable searchPlaceholder="Search departments…">
              <option value="">Not assigned</option>
              {options.departments.map(option)}
            </SelectControl>
          </Field>
          <Field id="contractor" label="Contractor" error={errors.contractor}>
            <SelectControl id="contractor" value={form.contractor || ''} onChange={setField('contractor')} searchable searchPlaceholder="Search contractors…">
              <option value="">Not assigned</option>
              {options.contractors.map(option)}
            </SelectControl>
          </Field>
          <Field id="po" label="PO number" error={errors.po_number}>
            <Input id="po" value={form.po_number} onChange={setField('po_number')} />
          </Field>
          <Field id="transfer-tank" label="Transfer tank" required={transferRequired} error={errors.tank}>
            <SelectControl
              id="transfer-tank"
              value={form.tank || ''}
              onChange={setField('tank')}
              disabled={!transferRequired}
              required={transferRequired}
            >
              <option value="">Select tank</option>
              {options.tanks.map(option)}
            </SelectControl>
          </Field>
        </div>
      </section>

      <section id="edit-operation" className="vd-edit-section vd-section-anchor">
        <div>
          <p className="vd-eyebrow">Fuel & operation</p>
          <h2>Operating configuration</h2>
        </div>
        <div className="vd-form-grid">
          <Field id="fuel" label="Fuel type" required error={errors.fuel_type}>
            <SelectControl id="fuel" value={form.fuel_type} onChange={setField('fuel_type')} required>
              <option value="">Select fuel</option>
              {ARTICLE_TYPE.map(option)}
            </SelectControl>
          </Field>
          <Field id="capacity" label="Tank capacity (litres)" required={isCreate} error={errors.tank_capacity}>
            <Input id="capacity" type="number" min="0" step="0.01" value={form.tank_capacity} onChange={setField('tank_capacity')} required={isCreate} />
          </Field>
          <Field id="tank-count" label="Number of fuel tanks" error={errors.no_of_tanks}>
            <Input id="tank-count" type="number" min="0" step="1" value={form.no_of_tanks} onChange={setField('no_of_tanks')} />
          </Field>
          <Field
            id="odometer"
            label={usesEngineHours ? 'Starting engine hours' : 'Starting odometer (km)'}
            required={isCreate}
            error={errors.odometer_reading}
          >
            <Input id="odometer" type="number" min="0" value={form.odometer_reading} onChange={setField('odometer_reading')} required={isCreate} />
          </Field>
          <Field id="mileage" label={usesEngineHours ? 'Standard fuel use (hr/L)' : 'Standard fuel efficiency (km/L)'} error={errors.standard_mileage}>
            <Input id="mileage" type="number" min="0" step="0.01" value={form.standard_mileage} onChange={setField('standard_mileage')} />
          </Field>
          <Field id="variance" label="Maximum allowed difference" error={errors.max_diff_allowed}>
            <Input id="variance" type="number" min="0" step="0.01" value={form.max_diff_allowed} onChange={setField('max_diff_allowed')} />
          </Field>
          {usesEngineHours && (
            <Field id="fixed-hours" label="Monthly fixed engine hours" error={errors.monthly_fixed_engine_hours}>
              <Input id="fixed-hours" type="number" min="0" step="0.01" value={form.monthly_fixed_engine_hours} onChange={setField('monthly_fixed_engine_hours')} />
            </Field>
          )}
          {isCreate && (
            <Field id="service-interval" label={`Service interval (${usesEngineHours ? 'hours' : 'km'}, optional)`} error={errors.service_interval}>
              <Input id="service-interval" type="number" min="0" value={form.service_interval} onChange={setField('service_interval')} />
            </Field>
          )}
        </div>
      </section>

      <section id="edit-telematics" className="vd-edit-section vd-section-anchor">
        <div>
          <p className="vd-eyebrow">Optional tracking</p>
          <h2>GPS and telematics</h2>
          <p className="vd-edit-help">Skip this section unless the vehicle sends data through a tracking provider.</p>
        </div>
        <details className="rounded-xl border border-slate-200 bg-slate-50/70 p-4" open={form.telemetics_enabled || undefined}>
          <summary className="cursor-pointer font-bold text-slate-800">Show tracking settings</summary>
          <div className="vd-form-grid mt-4">
          <label className="vd-switch-row" htmlFor="telematics-enabled">
            <input id="telematics-enabled" type="checkbox" checked={form.telemetics_enabled} onChange={setField('telemetics_enabled')} />
            <span>
              <strong>Telematics enabled</strong>
              <small>Allows stored provider projections to appear in the command centre.</small>
            </span>
          </label>
          <Field id="telematics-category" label="Telematics category" required={form.telemetics_enabled} error={errors.telemetics_category}>
            <SelectControl id="telematics-category" value={form.telemetics_category} onChange={setField('telemetics_category')} disabled={!form.telemetics_enabled} required={form.telemetics_enabled}>
              <option value="">Not recorded</option>
              {TELEMATICS_TYPES.map(option)}
            </SelectControl>
          </Field>
          <Field id="provider" label="Tracking provider" error={errors.fleet_service_provider}>
            <SelectControl id="provider" value={form.fleet_service_provider} onChange={setField('fleet_service_provider')} disabled={!form.telemetics_enabled}>
              <option value="">Not recorded</option>
              {PROVIDERS.map((item) => <option key={item} value={item}>{item}</option>)}
            </SelectControl>
          </Field>
          <label className="vd-switch-row" htmlFor="omnicomm-ignition">
            <input id="omnicomm-ignition" type="checkbox" checked={form.show_omnicomm_log_ignition} onChange={setField('show_omnicomm_log_ignition')} disabled={!form.telemetics_enabled} />
            <span><strong>Ignition</strong><small>Include ignition in Omnicomm log columns.</small></span>
          </label>
          <label className="vd-switch-row" htmlFor="omnicomm-engine">
            <input id="omnicomm-engine" type="checkbox" checked={form.show_omnicomm_log_engine} onChange={setField('show_omnicomm_log_engine')} disabled={!form.telemetics_enabled} />
            <span><strong>Engine</strong><small>Include engine in Omnicomm log columns.</small></span>
          </label>
          <label className="vd-switch-row" htmlFor="omnicomm-secondary">
            <input id="omnicomm-secondary" type="checkbox" checked={form.show_omnicomm_log_secondary_engine} onChange={setField('show_omnicomm_log_secondary_engine')} disabled={!form.telemetics_enabled} />
            <span><strong>Secondary engine</strong><small>Include secondary engine in Omnicomm log columns.</small></span>
          </label>
          <Field id="secondary-threshold" label="Secondary engine threshold" error={errors.omnicomm_secondary_engine_threshold}>
            <Input id="secondary-threshold" type="number" min="0" value={form.omnicomm_secondary_engine_threshold} onChange={setField('omnicomm_secondary_engine_threshold')} disabled={!form.telemetics_enabled || !form.show_omnicomm_log_secondary_engine} />
          </Field>
          </div>
        </details>
      </section>

      <ModulesSection
        checked={form.tyre_management_enabled}
        onCheckedChange={setCheckbox('tyre_management_enabled')}
        globalEnabled={globalTyreEnabled}
        enabledAt={tyreManagementEnabledAt}
      />
    </>
  );
};

VehicleFormSections.propTypes = {
  form: PropTypes.object.isRequired,
  setField: PropTypes.func.isRequired,
  setCheckbox: PropTypes.func.isRequired,
  options: PropTypes.shape({
    sites: PropTypes.array,
    groups: PropTypes.array,
    departments: PropTypes.array,
    contractors: PropTypes.array,
    tanks: PropTypes.array,
  }).isRequired,
  errors: PropTypes.object,
  assetCodeMappingEnabled: PropTypes.bool,
  isCreate: PropTypes.bool,
  originalType: PropTypes.string,
  globalTyreEnabled: PropTypes.bool,
  tyreManagementEnabledAt: PropTypes.string,
};

VehicleFormSections.defaultProps = {
  errors: {},
  assetCodeMappingEnabled: false,
  isCreate: false,
  originalType: '',
  globalTyreEnabled: false,
  tyreManagementEnabledAt: null,
};

export default VehicleFormSections;
